import type {
  ColDef,
  ColumnMovedEvent,
  GetRowIdFunc,
  GridReadyEvent,
  IGroupCellRendererParams,
  PaginationChangedEvent,
  RowHeightParams,
} from "ag-grid-enterprise";
import debounce from "lodash/debounce";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";
import { Icon, Skeleton } from "@web-app/components";
import Table from "@web-app/components/Table";
import {
  TableRow,
  getInitialTableData,
  routeNameValueGenerator,
} from "@web-app/pages/manage/data";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, OrderStatusText, Route, User } from "@web-app/types";
import { getRouteScheduledDates } from "@web-app/utils/helpers";

import { getRouteStatusText } from "../../DispatchMap/utils";

interface SidebarTableProps {
  assigneesMap: Map<string, User>;
  columnDefs: ColDef[];
  isLoading: boolean;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  setColumnDefs: (c: ColDef[]) => void;
  unroutedOrders: Order[];
}

const EmptyState = memo(function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="mb-4">
        <Icon name="boxesPacking" className="h-12 w-12 text-gray-300" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">
        No orders available
      </h3>
      <p className="text-sm text-gray-500">
        There are no orders for the selected filters.
      </p>
    </div>
  );
});

function SidebarTable({
  assigneesMap,
  columnDefs,
  isLoading,
  routedOrdersMap,
  routes,
  setColumnDefs,
  unroutedOrders,
}: SidebarTableProps) {
  const [isColumnStateLoaded, setIsColumnStateLoaded] = useState(false);
  const [gridApiRef, setGridApiRef] = useState<GridReadyEvent["api"] | null>(
    null,
  );

  const { customFields, isLoading: isCustomFieldsLoading } =
    useWorkspaceActiveCustomFields();

  const ordersList = useMemo(() => {
    const rows: TableRow[] = [];

    for (const route of routes) {
      const firstOrder = routedOrdersMap.get(route.ordersOrder[0]?.id);

      if (!firstOrder) {
        continue;
      }

      const scheduledDates = getRouteScheduledDates(route, routedOrdersMap);
      rows.push({
        ...route,
        assignee: route.assigneeId ? assigneesMap.get(route.assigneeId) : null,

        entity: "route",

        hasAttachments: route.ordersOrder.some(
          (routeOrder) =>
            (routedOrdersMap.get(routeOrder.id)?.attachments?.length ?? 0) > 0,
        ),

        scheduledEndDateTime: scheduledDates.endDate,

        scheduledStartDateTime: scheduledDates.startDate,
        // @ts-ignore - Need to fix
        statusText: getRouteStatusText(
          route?.status,
          !!route?.assigneeId,
        ) as OrderStatusText | null,
      });

      route.ordersOrder.forEach((o) => {
        const order = routedOrdersMap.get(o.id);
        if (order) {
          rows.push({
            ...order,
            assignee: order.assigneeId
              ? assigneesMap.get(order.assigneeId)
              : null,
            entity: "order",
          });
        }
      });
    }

    unroutedOrders.forEach((order: Order) => {
      rows.push({
        ...order,
        assignee: order.assigneeId ? assigneesMap.get(order.assigneeId) : null,
        entity: "order",
      });
    });

    return rows;
  }, [unroutedOrders, routedOrdersMap, assigneesMap, routes]);

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return {
      cellRendererParams: {
        checkbox: true,
        suppressCount: true,
      } as IGroupCellRendererParams,
      headerCheckboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true,
      headerName: "ID",
      lockPosition: "left",
      showDisabledCheckboxes: true,
      valueGetter: routeNameValueGenerator,
      width: 245,
    };
  }, []);

  const getDataPath = useCallback((data: TableRow) => {
    if (data.route) {
      return [data.route.id, data.id];
    }
    return [data.id];
  }, []);

  const updateGridState = () => {
    const columnState = localStorage.getItem("columnDefsDispatchPage");

    if (columnState) {
      gridApiRef?.applyColumnState({
        applyOrder: true,
        state: JSON.parse(columnState),
      });
    }
  };

  useEffect(() => {
    if (!isCustomFieldsLoading && !!gridApiRef) {
      setColumnDefs(getInitialTableData(customFields));
    }
  }, [isCustomFieldsLoading, gridApiRef]);

  useEffect(() => {
    if (columnDefs.length && !isColumnStateLoaded) {
      updateGridState();
      setIsColumnStateLoaded(true);
    }
  }, [columnDefs, isColumnStateLoaded]);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center gap-3 w-full px-4">
        <Skeleton height="h-[120px]" width="w-full" className="rounded-xl" />
        <Skeleton height="h-[120px]" width="w-full" className="rounded-xl" />
        <Skeleton height="h-[120px]" width="w-full" className="rounded-xl" />
        <Skeleton
          height="h-[120px]"
          width="w-full"
          className=" bg-gradient-to-b from-gray-200 !bg-white to-gray-50 rounded-xl"
        />
      </div>
    );
  }

  if (unroutedOrders.length === 0 && routedOrdersMap.size === 0) {
    return <EmptyState />;
  }

  const setColumnStates = () => {
    const columnState = gridApiRef?.getColumnState();
    localStorage.setItem("columnDefsDispatchPage", JSON.stringify(columnState));
  };

  // Table methods
  const getRowHeight = (params: RowHeightParams<Order>): number => {
    const pickupAddressLength = params.data?.pickup?.address.length ?? 0;
    const dropoffAddressLength = params.data?.dropoff?.address.length ?? 0;
    return pickupAddressLength > 50 || dropoffAddressLength > 50 ? 70 : 50;
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApiRef(params.api);
  };

  const onSelectionChange = () => {
    let selectedRows = gridApiRef?.getSelectedRows();
    if (selectedRows?.length) {
      const selectedRouteIds = selectedRows
        .filter((row) => row.entity === "route")
        .map((route) => route.id);
      useDispatchStore.setState({
        selectedSidebarRouteIds: selectedRouteIds,
      });
      selectedRows = selectedRows.filter((row) => row.entity === "order");
      if (selectedRows.length > 1) {
        useDispatchStore.setState({
          selectedSidebarOrderIds: selectedRows.map((row) => row.id),
        });
      } else if (selectedRows.length === 1) {
        const order = selectedRows[0];
        if (order && order.id) {
          useDispatchStore.setState({
            selectedSidebarOrderIds: [order.id],
          });
        }
      }
    } else {
      useDispatchStore.setState({
        selectedSidebarOrderIds: [],
        selectedSidebarRouteIds: [],
      });
    }
  };

  const handleUpdateVisibleColumns = () => {
    setColumnStates();
    const defs = gridApiRef?.getColumnDefs();
    // @ts-ignore - Need to fix
    setColumnDefs(defs);
  };

  const handleColumnMoved = (params: ColumnMovedEvent) => {
    if (params.source != "api") {
      const defs = gridApiRef?.getColumnDefs();
      // @ts-ignore - Need to fix
      setColumnDefs(defs);
    }
  };

  const handleColumnResize = debounce(() => {
    setColumnStates();
  }, 500);

  const onPaginationChanged = (params: PaginationChangedEvent) => {
    if (params.newData != undefined) {
      gridApiRef?.deselectAll();
    }
  };

  const getRowId: GetRowIdFunc<Order> = (params) => {
    return params.data.id;
  };

  return (
    <Table
      className="w-full px-4"
      rowData={ordersList}
      pagination
      paginationPageSizeSelector={[20, 50, 100, 1000]}
      paginationPageSize={20}
      suppressMovableColumns={true}
      rowSelection="multiple"
      suppressRowDeselection={true}
      onSelectionChanged={onSelectionChange}
      onColumnVisible={handleUpdateVisibleColumns}
      onColumnResized={handleColumnResize}
      treeData={true}
      getDataPath={getDataPath}
      groupSelectsChildren={true}
      getRowHeight={getRowHeight}
      autoGroupColumnDef={autoGroupColumnDef}
      onGridReady={onGridReady}
      onColumnMoved={handleColumnMoved}
      onPaginationChanged={onPaginationChanged}
      getRowId={getRowId}
      // @ts-ignore - Need to fix
      columnDefs={columnDefs}
    />
  );
}

export default SidebarTable;
